/**
 * ShareModal.jsx
 *
 * Modal for creating time-limited document share links.
 *
 * Branding: H Nawab (#CC0202 red, #262626 dark)
 */

import React, { useState } from "react";
import {
  X,
  Link2,
  Copy,
  Check,
  Clock,
  Lock,
  Eye,
  Download,
  Users,
  Building2,
  Scale,
  Shield,
  User,
} from "lucide-react";

const RECIPIENT_TYPES = [
  { value: "service_provider", label: "Service Provider", icon: Users, description: "Plumber, electrician, contractor" },
  { value: "buyer", label: "Buyer/Agent", icon: Building2, description: "Real estate transactions" },
  { value: "legal", label: "Legal", icon: Scale, description: "Lawyer, legal proceedings" },
  { value: "insurance", label: "Insurance", icon: Shield, description: "Claims, policy review" },
  { value: "custom", label: "Other", icon: User, description: "Custom recipient" },
];

const EXPIRY_OPTIONS = [
  { value: 1, label: "24 hours" },
  { value: 7, label: "7 days" },
  { value: 30, label: "30 days" },
  { value: 90, label: "90 days" },
  { value: null, label: "No expiry" },
];

export default function ShareModal({ isOpen, onClose, documents, propertyId, onShareCreated }) {
  const [selectedDocs, setSelectedDocs] = useState(documents.map((d) => d.id));
  const [recipientType, setRecipientType] = useState("service_provider");
  const [recipientName, setRecipientName] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [expiresIn, setExpiresIn] = useState(7);
  const [maxAccesses, setMaxAccesses] = useState("");
  const [password, setPassword] = useState("");
  const [usePassword, setUsePassword] = useState(false);
  const [permissions, setPermissions] = useState(["view"]);
  const [isCreating, setIsCreating] = useState(false);
  const [createdShare, setCreatedShare] = useState(null);
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const toggleDocSelection = (docId) => {
    setSelectedDocs((prev) =>
      prev.includes(docId) ? prev.filter((id) => id !== docId) : [...prev, docId]
    );
  };

  const togglePermission = (perm) => {
    setPermissions((prev) =>
      prev.includes(perm) ? prev.filter((p) => p !== perm) : [...prev, perm]
    );
  };

  const handleCreate = async () => {
    if (selectedDocs.length === 0) return;

    setIsCreating(true);
    try {
      const res = await fetch("/api/shares", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          documentIds: selectedDocs,
          propertyId,
          recipientType,
          recipientName: recipientName || null,
          recipientEmail: recipientEmail || null,
          expiresIn,
          maxAccesses: maxAccesses ? parseInt(maxAccesses) : null,
          password: usePassword && password ? password : null,
          permissions,
        }),
      });

      if (!res.ok) throw new Error("Failed to create share");

      const share = await res.json();
      setCreatedShare(share);
      if (onShareCreated) onShareCreated(share);
    } catch (err) {
      console.error("Error creating share:", err);
      alert("Failed to create share link");
    } finally {
      setIsCreating(false);
    }
  };

  const copyToClipboard = () => {
    const url = `${window.location.origin}/share/${createdShare.accessToken}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = () => {
    setCreatedShare(null);
    setSelectedDocs(documents.map((d) => d.id));
    setRecipientName("");
    setRecipientEmail("");
    setPassword("");
    setUsePassword(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Link2 className="w-5 h-5 text-hn-red" />
            <h2 className="text-lg font-semibold text-hn-dark">
              {createdShare ? "Share Link Created" : "Share Documents"}
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          {createdShare ? (
            /* Success View */
            <div className="space-y-4">
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-emerald-600" />
                </div>
                <p className="text-gray-600 mb-2">Your share link is ready</p>
              </div>

              {/* Link Box */}
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border">
                <input
                  type="text"
                  readOnly
                  value={`${window.location.origin}/share/${createdShare.accessToken}`}
                  className="flex-1 bg-transparent text-sm text-gray-700 outline-none truncate"
                />
                <button
                  onClick={copyToClipboard}
                  className="flex items-center gap-1 px-3 py-1.5 bg-hn-red text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>

              {/* Share Details */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Expires</p>
                  <p className="font-medium text-gray-700">
                    {createdShare.expiresAt
                      ? new Date(createdShare.expiresAt).toLocaleDateString()
                      : "Never"}
                  </p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Max Views</p>
                  <p className="font-medium text-gray-700">
                    {createdShare.maxAccesses || "Unlimited"}
                  </p>
                </div>
              </div>

              {createdShare.hasPassword && (
                <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <Lock className="w-4 h-4 text-amber-600" />
                  <p className="text-sm text-amber-700">
                    This link is password protected
                  </p>
                </div>
              )}
            </div>
          ) : (
            /* Create Share Form */
            <div className="space-y-5">
              {/* Document Selection */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Select Documents ({selectedDocs.length} selected)
                </label>
                <div className="space-y-2 max-h-32 overflow-y-auto border rounded-lg p-2">
                  {documents.map((doc) => (
                    <label
                      key={doc.id}
                      className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedDocs.includes(doc.id)}
                        onChange={() => toggleDocSelection(doc.id)}
                        className="w-4 h-4 text-hn-red rounded border-gray-300 focus:ring-hn-red"
                      />
                      <span className="text-sm text-gray-700 truncate">
                        {doc.title || doc.fileName}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Recipient Type */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Sharing With
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {RECIPIENT_TYPES.map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setRecipientType(type.value)}
                      className={`flex items-center gap-2 p-3 rounded-lg border transition-colors text-left ${
                        recipientType === type.value
                          ? "border-hn-red bg-red-50"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <type.icon className={`w-4 h-4 ${recipientType === type.value ? "text-hn-red" : "text-gray-400"}`} />
                      <div>
                        <p className="text-sm font-medium text-gray-700">{type.label}</p>
                        <p className="text-xs text-gray-500">{type.description}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Recipient Details */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    Recipient Name
                  </label>
                  <input
                    type="text"
                    value={recipientName}
                    onChange={(e) => setRecipientName(e.target.value)}
                    placeholder="e.g., John's Plumbing"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-hn-red/20 focus:border-hn-red"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    Email (optional)
                  </label>
                  <input
                    type="email"
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                    placeholder="email@example.com"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-hn-red/20 focus:border-hn-red"
                  />
                </div>
              </div>

              {/* Expiry */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                  <Clock className="w-4 h-4 text-gray-400" />
                  Link Expires In
                </label>
                <div className="flex flex-wrap gap-2">
                  {EXPIRY_OPTIONS.map((opt) => (
                    <button
                      key={opt.value ?? "none"}
                      onClick={() => setExpiresIn(opt.value)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        expiresIn === opt.value
                          ? "bg-hn-red text-white"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Max Accesses */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  Max Views (optional)
                </label>
                <input
                  type="number"
                  value={maxAccesses}
                  onChange={(e) => setMaxAccesses(e.target.value)}
                  placeholder="Unlimited"
                  min="1"
                  className="w-32 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-hn-red/20 focus:border-hn-red"
                />
              </div>

              {/* Password Protection */}
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={usePassword}
                    onChange={(e) => setUsePassword(e.target.checked)}
                    className="w-4 h-4 text-hn-red rounded border-gray-300 focus:ring-hn-red"
                  />
                  <Lock className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-700">Password protect</span>
                </label>
                {usePassword && (
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    className="mt-2 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-hn-red/20 focus:border-hn-red"
                  />
                )}
              </div>

              {/* Permissions */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Permissions
                </label>
                <div className="flex gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={permissions.includes("view")}
                      onChange={() => togglePermission("view")}
                      className="w-4 h-4 text-hn-red rounded border-gray-300 focus:ring-hn-red"
                    />
                    <Eye className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-700">View</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={permissions.includes("download")}
                      onChange={() => togglePermission("download")}
                      className="w-4 h-4 text-hn-red rounded border-gray-300 focus:ring-hn-red"
                    />
                    <Download className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-700">Download</span>
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-4 border-t border-gray-200">
          {createdShare ? (
            <button
              onClick={handleClose}
              className="px-4 py-2 bg-hn-red text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
            >
              Done
            </button>
          ) : (
            <>
              <button
                onClick={handleClose}
                className="px-4 py-2 text-gray-600 font-medium rounded-lg hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={selectedDocs.length === 0 || isCreating}
                className="px-4 py-2 bg-hn-red text-white font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Link2 className="w-4 h-4" />
                {isCreating ? "Creating..." : "Create Link"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
